import { useCallback, useEffect, useState } from "react";
import { ParamType } from "../../../types/grid.ts";
import type { AdvancedFilterProps } from "./types.ts";

export function TextBoxParamFilter({ info, isParameter, value, onChange, onSearch }: AdvancedFilterProps) {
  // FilterTextBoxLike wants substring match (%text%, ParamType.Like) — matches
  // WebAdmin2's FilterTextBoxLike behaviour. FilterTextBox keeps the exact
  // match (ParamType.Specified, value as-is).
  //
  // A function/SP parameter (e.g. aquirerReport's @AuthCodes, an
  // S_String_TableType) is always ParamType.Specified: the server only binds
  // parameters on that type, and splits a comma list into the table-valued
  // parameter itself. In or Like there is looked up as a view column instead
  // and rejected ("Column.P 'AuthCodes' is not defined").
  const isLike = !isParameter && info.FilterType?.toUpperCase() === "FILTERTEXTBOXLIKE";

  /** Strip surrounding % wildcards when rehydrating from an existing value. */
  const unwrapLike = (raw: string): string => {
    let out = raw;
    if (out.startsWith("%")) out = out.slice(1);
    if (out.endsWith("%")) out = out.slice(0, -1);
    return out;
  };

  const [text, setText] = useState(() => {
    if (value.length > 0 && value[0]!.oValue != null) {
      const raw = String(value[0]!.oValue);
      // ParamType.In keeps the user's typed comma list verbatim — no %-wrap
      // to strip. Only Like values were wrapped on emit.
      if (value[0]!.Type === ParamType.In) return raw;
      return value[0]!.Type === ParamType.Like ? unwrapLike(raw) : raw;
    }
    return "";
  });

  useEffect(() => {
    if (value.length === 0) setText("");
  }, [value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      setText(v);
      const trimmed = v.trim();
      if (!trimmed) {
        onChange([]);
        return;
      }
      // Comma in the input → user intends a list. Split, sanitise, emit
      // as ParamType.In (server-side OR-match across the entries). Even
      // a single-entry trailing-comma input ("foo,") is treated as In
      // since the comma is a deliberate signal of list intent. %-wrap
      // doesn't apply here — Like and In compose poorly. An input that
      // is *only* commas yields zero parts and clears the filter.
      // Parameters keep Specified with the same trimmed list — the server's
      // table-type split does not trim, so "a, b" would otherwise send " b".
      if (trimmed.includes(",")) {
        const parts = trimmed.split(",").map((s) => s.trim()).filter(Boolean);
        if (parts.length === 0) {
          onChange([]);
        } else {
          const Type = isParameter ? ParamType.Specified : ParamType.In;
          onChange([{ sColumn: info.ColumnName, oValue: parts.join(","), Type }]);
        }
        return;
      }
      const oValue = isLike ? `%${trimmed}%` : v;
      const Type = isLike ? ParamType.Like : ParamType.Specified;
      onChange([{ sColumn: info.ColumnName, oValue, Type }]);
    },
    [info.ColumnName, onChange, isLike, isParameter],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") onSearch();
    },
    [onSearch],
  );

  // A filled text box always means someone typed a search — these have no
  // defaults. Ring it in the brand colour so a value still sitting in the bar
  // (after an endpoint switch, or on returning to the page) stands out.
  const filled = text.trim() !== "";

  return (
    <input
      type="text"
      value={text}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder={info.FilterText || info.Header || info.ColumnName}
      title={text || undefined}
      style={{
        width: "140px",
        padding: "2px 4px",
        fontSize: "var(--text-sm)",
        fontFamily: "var(--font-family-input)",
        border: `1px solid var(${filled ? "--color-primary" : "--color-border-default"})`,
        boxShadow: filled ? "0 0 0 1px var(--color-primary)" : undefined,
        fontWeight: filled ? "var(--font-medium)" : undefined,
        borderRadius: "var(--radius-1)",
        background: "var(--color-bg)",
        color: "var(--color-text)",
      }}
    />
  );
}
